/**
 * schemaTypes.js — single source of truth for classifying a backend schema
 * into a "schema_type".
 *
 * Background: the app stores data in several backend schemas ("tracking",
 * "acc", "single", ...). The `schema` string identifies the physical
 * namespace (where the table/parquet lives) and must be sent to the backend
 * verbatim so it can locate the data.
 *
 * Separately, some schemas hold MULTIPLE WAVES of the same questions (a time
 * series) while others hold a single snapshot. The frontend and backend need
 * to branch on that distinction (show the wave selector, add `wave` to the
 * GROUP BY, etc.). Historically that branch was hard-coded as
 * `schema === "tracking"`. Now that ACC also has multiple waves, we classify
 * schemas into a `schema_type` — "tracking" (multi-wave) or "single" — and
 * branch on that instead.
 *
 * To onboard a new multi-wave schema in future, add it to TRACKING_SCHEMAS.
 * Everything else (frontend gating + the `schema_type` posted to the backend)
 * follows automatically.
 */

/** Schemas that hold multiple waves and should behave like "tracking". */
export const TRACKING_SCHEMAS = new Set(["tracking", "acc"]);

export const SCHEMA_TYPE_TRACKING = "tracking";
export const SCHEMA_TYPE_SINGLE = "single";

/**
 * Classify a raw schema string into its schema_type.
 * @param {string|undefined|null} schema  e.g. "tracking", "acc", "single", "public"
 * @returns {"tracking"|"single"}
 */
export function schemaTypeFor(schema) {
  return TRACKING_SCHEMAS.has(schema) ? SCHEMA_TYPE_TRACKING : SCHEMA_TYPE_SINGLE;
}

/** True when the schema holds multiple waves (was: schema === "tracking"). */
export function isTrackingSchema(schema) {
  return schemaTypeFor(schema) === SCHEMA_TYPE_TRACKING;
}

/**
 * Convenience for the common `group.schema === "tracking"` group-level check.
 * @param {{schema?: string}|undefined|null} group
 */
export function isMultiWaveGroup(group) {
  return isTrackingSchema(group?.schema);
}
